/**
 * DriverAvailableOrders — driver-facing marketplace where they can browse and
 * claim unassigned orders near them (Uber-driver style).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, MapPin, Loader2, Store, Truck, Zap } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PriorityCountdown from './PriorityCountdown';
import DeliveryFeeBreakdown from '../components/DeliveryFeeBreakdown';
import { formatMoney } from '../lib/money';

const DriverAvailableOrders = () => {
  const [items, setItems] = useState([]);
  const [zone, setZone] = useState(null);
  const [isSuper, setIsSuper] = useState(false);
  const [coverageCount, setCoverageCount] = useState(0);
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/driver/orders/available').then((res) => res.data);
      setItems(r.orders || []);
      setZone(r.zone || null);
      setIsSuper(Boolean(r.is_super_driver));
      setCoverageCount((r.coverage_zone_ids || []).length);
      setCity(r.city || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load orders');
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 10s so the 60s priority window and stale-order clock
  // stay accurate without the driver having to hit Refresh. Pause when the
  // tab is hidden to avoid burning battery + backend cycles.
  useEffect(() => {
    let timer;
    const tick = () => {
      if (document.visibilityState === 'visible') load();
    };
    timer = setInterval(tick, 10000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  const claim = async (orderId) => {
    setClaiming(orderId);
    try {
      await api.post(`/driver/orders/${orderId}/claim`);
      toast.success('Order claimed! Head to the store.');
      setItems((arr) => arr.filter((o) => o.order_id !== orderId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim this order');
      load();
    } finally { setClaiming(null); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="driver-available-orders">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            Available orders near you
            {isSuper && (
              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-lime-400 text-emerald-900 px-2 py-1 rounded-full shadow" data-testid="super-driver-flag">
                <Zap className="h-3 w-3" /> Super Driver · {city} city-wide
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isSuper
              ? <>You&apos;re a Super Driver — you can pick up any order in {city}, including stalled ones nobody else claimed.</>
              : <>Grab any order below to earn on your own schedule. Once you claim it, you have <b>3 minutes</b> to start heading out — otherwise the order goes back to the pool.</>}
          </p>
          {!isSuper && zone && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1" data-testid="driver-current-zone">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: zone.color }} />
              You are in <span className="font-semibold">{zone.name}, {zone.city}</span>
              {coverageCount > 0 && <span className="text-slate-400 ml-1">· covering {coverageCount} zones</span>}
            </div>
          )}
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-available-btn">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 mb-4" data-testid="available-error">
          {error}
        </div>
      )}

      {loading && !items.length ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : items.length === 0 && !error ? (
        <div className="py-16 text-center text-slate-500" data-testid="available-empty">
          <Truck className="h-8 w-8 mx-auto text-slate-300" />
          <div className="mt-2 font-medium">No orders waiting nearby.</div>
          <div className="text-xs">We&apos;ll ping you as soon as one pops up.</div>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((o) => (
            <li key={o.order_id}
                className={`bg-white border rounded-2xl p-4 ${o.is_priority ? 'border-emerald-400 ring-2 ring-emerald-200 shadow-sm shadow-emerald-100' : o.is_stale ? 'border-amber-300 ring-2 ring-amber-100' : 'border-slate-200'}`}
                data-testid={`available-order-${o.order_id}`}>
              <div className="flex items-center gap-4">
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${o.is_priority ? 'bg-emerald-600 text-white' : o.is_stale ? 'bg-amber-500 text-white' : 'bg-emerald-50 text-emerald-700'}`}>
                {o.is_priority || o.is_stale ? <Zap className="h-5 w-5" /> : <Store className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                  {String(o.vendor_id || '').startsWith('sys-') ? 'ShipitNow delivery' : o.vendor_name}
                  {o.vendor_category && ['courier', 'package', 'logistics'].includes(o.vendor_category) && (
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                        o.vendor_category === 'courier' ? 'bg-blue-100 text-blue-700 border border-blue-300' :
                        o.vendor_category === 'package' ? 'bg-sky-100 text-sky-700 border border-sky-300' :
                        'bg-amber-100 text-amber-800 border border-amber-300'
                      }`}
                      data-testid={`available-category-badge-${o.order_id}`}
                    >
                      {o.vendor_category === 'courier' ? 'Courier' : o.vendor_category === 'package' ? 'Same-day' : 'Logistics'}
                    </span>
                  )}
                  {o.is_priority && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded-full" data-testid={`priority-badge-${o.order_id}`}>
                      Priority · {
                        o.priority_source === 'customer' ? 'Customer pick'
                        : o.priority_source === 'admin_reassign' ? 'Admin reassigned to you'
                        : o.priority_source === 'vendor_reassign' ? 'Vendor reassigned to you'
                        : 'Vendor pick'
                      }
                    </span>
                  )}
                  {o.is_priority && o.priority_expires_at && (
                    <PriorityCountdown
                      expiresAt={o.priority_expires_at}
                      onExpire={load}
                      testid={`priority-countdown-${o.order_id}`}
                    />
                  )}
                  {!o.is_priority && o.is_stale && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded-full" data-testid={`stale-badge-${o.order_id}`} title="Waiting more than 20 min for a driver">
                      Stale · {o.minutes_waiting} min waiting
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <MapPin className="h-3 w-3" /> {o.distance_km} km away · {o.items_count} items · ETA {o.eta || '20–30 min'}
                  {o.zone_name && (
                    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium border" style={{ backgroundColor: `${o.zone_color}22`, borderColor: o.zone_color, color: o.zone_color }} data-testid={`available-zone-${o.order_id}`}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: o.zone_color }} />
                      {o.zone_name}
                    </span>
                  )}
                  {(o.broadcast_cycles || 1) > 1 && (
                    <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5" title="Previously released by another driver">
                      Re-broadcast · {o.broadcast_cycles}×
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-emerald-700" data-testid={`available-earn-${o.order_id}`}>
                  +{formatMoney(o.driver_amount || 0, o.currency)}
                </div>
                <div className="text-[11px] text-slate-400">your earnings</div>
              </div>
              <Button
                onClick={() => claim(o.order_id)}
                disabled={claiming === o.order_id}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`claim-btn-${o.order_id}`}
              >
                {claiming === o.order_id ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />}
                Claim
              </Button>
              </div>

              {o.pay_for_me && (
                <div className="mt-3 rounded-xl bg-indigo-50 border border-indigo-200 px-3 py-2 text-xs text-indigo-800 font-medium" data-testid={`driver-payforme-note-${o.order_id}`}>
                  PAY FOR ME — pay the store {formatMoney(o.pay_for_me_goods_amount || 0, o.currency)} for the goods on arrival,
                  then deliver. The vendor&rsquo;s payment confirmation releases the order — no QR scan needed.
                </div>
              )}
              {/* Trip pay fee card — the same km + fee breakdown the customer sees */}
              <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid={`driver-fee-card-${o.order_id}`}>
                <div className="flex items-center justify-between text-xs font-semibold text-slate-700">
                  <span>Trip pay breakdown</span>
                  <span data-testid={`driver-fee-card-delivery-${o.order_id}`}>Delivery fee {formatMoney(o.delivery_fee || 0, o.currency)}</span>
                </div>
                <DeliveryFeeBreakdown
                  breakdown={o.delivery_fee_breakdown}
                  distanceKm={o.trip_km}
                  currency={o.currency}
                  className="mt-1.5"
                />
                <div className="flex items-center justify-between text-xs text-slate-500 mt-1.5">
                  <span>Service fee (ShipitNow)</span>
                  <span>{formatMoney(o.service_fee || 0, o.currency)}</span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1.5 pt-1.5 border-t border-slate-200">
                  <span className="text-slate-500">
                    You earn{o.driver_share_pct ? ` · ${Number(o.driver_share_pct)}% of the delivery fee` : ''}
                  </span>
                  <span className="font-bold text-emerald-700" data-testid={`driver-fee-card-earn-${o.order_id}`}>
                    +{formatMoney(o.driver_amount || 0, o.currency)}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DriverAvailableOrders;
